import { Resource } from '../models/Resource'
import { getParentOfType, Instance } from 'mobx-state-tree'
import { Topic } from '../models/Topic'
import { Quiz } from '../models/Quiz'
import { TopicHelper } from './TopicHelper'
import { QuizHelper } from './QuizHelper'
import { ResourceApi } from '../services/api-objects/ResourceApi'

export class ResourceHelper {
  static fetchedResourcesToResources(fetchedResources: any): Array<Instance<typeof Resource>> {
    if (!fetchedResources?.length) {
      console.warn(`objectsToResources was given an array of length 0`)
      return [] as Array<Instance<typeof Resource>>
    }

    const identityBreaker = Math.random() * 999999999

    return fetchedResources.map((fr) => {
      return Resource.create({
        source_id: fr.source_id,
        resource_id: fr.resource_id,
        created_at: fr.created_at,
        filename_or_url: fr.filename_or_url,
        is_required: false,
        title: fr.title,
        file_size: fr.file_size,
        mime_type: fr.mime_type,
        identityBreaker,
        extension: fr.extension,
        processing_status: fr.processing_status,
      })
    })
  }

  static getResourceReadToResource(fetchedResources: any): any {
    if (!fetchedResources?.length) {
      console.warn(`objectsToResources was given an array of length 0`)
      return [] as Array<Instance<typeof Resource>>
    }

    const identityBreaker = Math.random() * 999999999

    const fetched = fetchedResources.map((fr: any) => {
      const source_title = fr.columns.find((column) => column.field === 'source_title')
      const source_created_date = fr.columns.find(
        (column) => column.field === 'source_created_date',
      )
      const source_filename_url = fr.columns.find(
        (column) => column.field === 'source_filename_url',
      )
      const source_file_size = fr.columns.find((column) => column.field === 'source_file_size')
      const source_file_type = fr.columns.find((column) => column.field === 'source_mimetype')

      return Resource.create({
        source_id: fr.id,
        resource_id: fr.resource_id,
        created_at: source_created_date.value,
        filename_or_url: source_filename_url.value,
        is_required: false,
        title: source_title.value,
        file_size: source_file_size.value,
        mime_type: source_file_type.value,
        identityBreaker,
      })
    })

    return fetched
  }

  static getQuizResourceToResources(fetchedResources: any): Array<Instance<typeof Resource>> {
    if (!fetchedResources?.length) {
      console.warn(`objectsToResources was given an array of length 0`)
      return [] as Array<Instance<typeof Resource>>
    }

    const identityBreaker = Math.random() * 999999999

    const fetched = fetchedResources.map((fr) => {
      const source_title = fr.columns.find((column) => column.field === 'source_title')
      const source_created_date = fr.columns.find(
        (column) => column.field === 'source_created_date',
      )
      const source_filename_url = fr.columns.find(
        (column) => column.field === 'source_filename_url',
      )
      const source_file_size = fr.columns.find((column) => column.field === 'source_file_size')
      const source_file_type = fr.columns.find((column) => column.field === 'source_file_type')
      const source_mimetype = fr.columns.find((column) => column.field === 'source_mimetype')
      const topic_source_required = fr.columns.find(
        (column) => column.field === 'topic_source_required',
      )
      const quiz_source_required = fr.columns.find(
        (column) => column.field === 'quiz_source_required',
      )
      const processing_status = fr.columns.find(
        (column) => column.field === 'source_processing_status',
      )
      const r = Resource.create({
        source_id: fr.id,
        extension: source_file_type.value,
        resource_id: fr.resource_id,
        created_at: source_created_date.value,
        filename_or_url: source_filename_url.value,
        is_required: quiz_source_required.value ?? false,
        title: source_title.value,
        file_size: source_file_size.value,
        mime_type: source_mimetype.value,
        processing_status: processing_status.value,
        identityBreaker,
      })
      return r
    })
    return fetched
  }

  static updateResource(resource: Instance<typeof Resource>) {
    const updateResourcePromise = ResourceApi.updateResource(resource)

    let parentTopic = null as any
    let parentQuiz = null as any
    try {
      parentTopic = getParentOfType(resource, Topic)
      return TopicHelper.updateResources(parentTopic, resource)
    } catch {
      // do nothing
    }

    try {
      if (parentTopic) {
        return
      }
      parentQuiz = getParentOfType(resource, Quiz)
      return QuizHelper.updateResources(parentQuiz, resource)
    } catch {
      // do nothing
    }

    return updateResourcePromise
  }

  static async getTopicsQuizsIds(resourceIds: number[]) {
    const topicsResponse = resourceIds.map((resourceId) => {
      return ResourceApi.getTopicsOfResource(resourceId)
    })
    const quizzesResponse = resourceIds.map((resourceId) => {
      return ResourceApi.getQuizzesOfResource(resourceId)
    })
    const [topics, quizzes] = await Promise.all([
      Promise.all(topicsResponse),
      Promise.all(quizzesResponse),
    ])
    const topicIds = [
      ...new Set(
        topics
          .map((topic) => topic.data)
          .flat()
          .map((topic) => topic?.topic_id),
      ),
    ]
    const quizIds = [
      ...new Set(
        quizzes
          .map((quiz) => quiz.data)
          .flat()
          .map((quiz) => quiz?.quiz_id),
      ),
    ]
    return [topicIds, quizIds]
  }
}
